'use client';

// `<MainnetConstructionModal />` — shown when a user tries to toggle out
// of demo mode while `mainnet_enabled` is off. Same composition as the rest
// of the static info modals (title + description + icon list + footer),
// with the per-row icon badges carrying status-tinted colors so the roadmap
// state (done / active / pending) is visible at a glance.
//
// Mounted once at the app-shell level. State lives in a global Zustand
// store so any callsite (wallet-dropdown toggle, demo-not-found card,
// etc.) can trigger the same single modal.

import { useTranslations } from 'next-intl';

import { DocsLinks } from '@/components/how-it-works/docs-links';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Rocket01Icon, Shield01Icon, UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { cn } from '@/lib/ui/utils';
import { useMainnetConstructionModalStore } from '@/stores/mainnet-construction-modal';

export function MainnetConstructionModal(): React.ReactNode {
  const t = useTranslations('mainnetConstruction');
  const open = useMainnetConstructionModalStore((s) => s.open);
  const setOpen = useMainnetConstructionModalStore((s) => s.setOpen);

  function handleClose(): void {
    setOpen(false);
  }

  return (
    <InfoModal
      open={open}
      onOpenChange={setOpen}
      size="default"
      title={t('title')}
      description={t('description')}
      footer={
        <InfoModal.Footer
          cta={t('cta')}
          onClose={handleClose}
          primaryHref="/about"
          secondary={{ label: t('dismiss'), onClick: handleClose }}
        />
      }
    >
      <InfoModal.IconList>
        <StatusItem
          status="done"
          icon={UserMultipleIcon}
          title={t('betaTitle')}
          description={t('betaDescription')}
        />
        <StatusItem
          status="active"
          icon={Shield01Icon}
          title={t('auditTitle')}
          description={t('auditDescription')}
        />
        <StatusItem
          status="pending"
          icon={Rocket01Icon}
          title={t('launchTitle')}
          description={t('launchDescription')}
        />
      </InfoModal.IconList>
      <DocsLinks docsPath="/safety-and-risk" />
    </InfoModal>
  );
}

interface StatusItemProps {
  status: 'done' | 'active' | 'pending';
  icon: IconSvgElement;
  title: string;
  description: string;
}

// Matches the layout/typography of <InfoModal.IconItem>. Only the icon-badge
// background and icon color vary per roadmap status — the rest is aligned
// with every other static info modal in the workspace bench.
function StatusItem({ status, icon, title, description }: StatusItemProps): React.ReactNode {
  return (
    <div className="flex items-start gap-4">
      <div
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-card',
          status === 'done' && 'bg-feedback-success-accent/15',
          status === 'active' && 'bg-foreground-brand/15',
          status === 'pending' && 'bg-background-tertiary',
        )}
      >
        <Icon
          icon={icon}
          className={cn(
            'size-5',
            status === 'done' && 'text-feedback-success-content',
            status === 'active' && 'text-foreground-brand',
            status === 'pending' && 'text-foreground-tertiary',
          )}
        />
      </div>
      <div>
        <h3 className="text-heading-xs text-foreground-primary">{title}</h3>
        <p className="text-body text-foreground-secondary">{description}</p>
      </div>
    </div>
  );
}
